import { ChangeEvent, useState } from "react";
import InputBox from "../../Input/Input";
import Modal from "../../Modal/Modal";
import Button from "../../Button/Button";
import {
  SaveUserName,
  VerifyUserName,
} from "../../../services/username.service";
import useAuthService from "../../../auth/AuthService";
import { username } from "../../../types/types";
import "./UserNameModal.css";
import Label from "../../Label/Label";

export const UserNameModal: React.FC<{
  onClose?: () => void;
  onClick?: () => void;
  isVisible?: boolean;
  isBuy?: boolean;
}> = ({ onClose, isVisible, onClick, isBuy = false }) => {
  const { getToken } = useAuthService();
  const [formData, setFormData] = useState({
    username: "",
    isAvailable: false,
    errors: "",
    msg: "",
  });

  const validateForm = () => {
    const newErrors: any = {};
    if (!formData.username.trim()) {
      newErrors.username = "Username is required.";
    }
    setFormData((pre) => ({ ...pre, errors: newErrors.username }));
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      errors: "",
    }));
    if (value.length === 0) {
      setFormData((prev) => ({
        ...prev,
        isAvailable: false,
        errors: "",
      }));
    }
    if (value.length <= 20) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      if (value.length < 3) {
        setFormData((prev) => ({
          ...prev,
          isAvailable: false,
          errors: "Username must be 3-20 characters",
        }));
      } else {
        if (/\s/.test(value)) {
          setFormData((prev) => ({
            ...prev,
            isAvailable: false,
            errors: "Spaces are not allowed.",
          }));
        } else {
          // 2. Check for emojis (basic check)
          if (/[^a-zA-Z0-9_-]/.test(value)) {
            setFormData((prev) => ({
              ...prev,
              isAvailable: false,
              errors:
                "Emojis and special characters (except _ and -) are not allowed.",
            }));
          } else {
            // 3. Fully numeric usernames are not allowed
            if (/^\d+$/.test(value)) {
              setFormData((prev) => ({
                ...prev,
                isAvailable: false,
                errors: "Username cannot be numbers only.",
              }));
            } else {
              // 4. Only allows letters, numbers, `_` and `-`
              if (!/^[a-zA-Z0-9_-]+$/.test(value)) {
                setFormData((prev) => ({
                  ...prev,
                  isAvailable: false,
                  errors:
                    "Username must contain only letters, numbers, - or _.",
                }));
              } else {
                // 5. No underscore at the start or end
                if (/^_|_$/.test(value)) {
                  setFormData((prev) => ({
                    ...prev,
                    isAvailable: false,
                    errors: "Underscores (_) cannot be at the start or end.",
                  }));
                } else {
                  handleUserName(value);
                }
              }
            }
          }
        }
      }
    }
  };

  const handleUserName = async (value: string) => {
    const username: username = { username: value };
    const response = await VerifyUserName(getToken, username);
    if (response.error != null) {
      setFormData((prev) => ({
        ...prev,
        isAvailable: false,
        errors: response.error as string,
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      isAvailable: true,
      errors: "",
      msg: response.data.message,
    }));
  };

  const handleSaveChanges = async () => {
    if (formData.isAvailable) {
      if (validateForm() && onClick) {
        const username: username = { username: formData.username };
        const response = await SaveUserName(getToken, username);
        if (response.error != null) {
          setFormData((pre) => ({ ...pre, errors: response.error as string }));
          return;
        }
        onClick();
        setFormData({
          isAvailable: false,
          username: "",
          errors: "",
          msg: response.data.message,
        });
      }
    } else {
      setFormData((pre) => ({ ...pre, errors: "Username is not Available." }));
    }
  };

  const handleClose = () => {
    if (onClose) {
      setFormData({
        username: "",
        isAvailable: false,
        errors: "",
        msg: "",
      });
      onClose();
    }
  };

  return (
    <Modal
      id="UserNameModal"
      isVisible={isVisible}
      modalBackground={`modal-bg ${isBuy ? "modal-auction" : "modal-buy"}`}
      onClose={handleClose}
      title={"Choose Username"}
      body={
        <div className="modal-body bg-modalbody m-3 pt-0 mt-0">
          <div className="mb-3">
            <div className="username-warning">
              <div>
                Your username is viewable by all users and will be seen by the
                famous people you interact with.
              </div>
              <div className="mt-2">It cannot be changed.</div>
            </div>
            <Label
              className="col-form-label font-inter-400-14"
              text="Username"
              required={false}
            />
            <div className="w-100 position-relative">
              <InputBox
                onChange={handleInputChange}
                className={`form-control application-form-control font-inter-400-14 ${
                  formData.username.length > 0 &&
                  (formData.isAvailable
                    ? "border border-success border-2"
                    : "border border-danger border-2")
                }`}
                name="username"
                value={formData.username}
              />
              <div className="verify-tag">
                {formData.username.length > 0 &&
                  (formData.isAvailable ? (
                    <i className="fa-regular fa-circle-check text-success"></i>
                  ) : (
                    <i className="fa-regular fa-circle-xmark text-danger"></i>
                  ))}
              </div>
            </div>
            {formData.username.length > 0 && (
              <>
                {formData.errors && (
                  <div className="error-msg">{formData.errors}</div>
                )}
                {formData.isAvailable && (
                  <div className="success-msg">{formData.msg}</div>
                )}
              </>
            )}

            <Button
              label={"Continue"}
              onClick={() => formData.isAvailable && handleSaveChanges()}
              className={`submit-btn ${!formData.isAvailable && "disable-btn"}`}
            />
          </div>
        </div>
      }
    />
  );
};
